/**
 * Tree visualization and layout algorithms
 * Functions for organizing nodes into columns and calculating positions
 */

import { TodoTree, TodoItem, findRootNodeId } from '../../types/todo';
import { getItemById } from './queries';
import { LAYOUT } from '../constants';

/**
 * Represents a node positioned in a column for tree visualization
 * Contains both the original todo item data and calculated positioning information
 */
export interface ColumnNode {
  id: string;
  item: TodoItem;
  /** ID of the parent node, undefined for root nodes */
  parentId?: string;
  /** Array of child node IDs that are currently visible */
  childrenIds: string[];
  /** Array of archived child node IDs that should be shown in compact view */
  archivedChildrenIds: string[];
  /** Calculated Y position for rendering (in pixels) */
  yPosition?: number;
  /** Total height needed for this node's entire subtree (in pixels) */
  subtreeHeight?: number;
}

/**
 * Represents a column in the tree visualization
 * Each column contains nodes at the same hierarchical depth
 */
export interface TreeColumn {
  /** Depth level (0 = root, 1 = first children, etc.) */
  depth: number;
  /** Array of nodes positioned in this column */
  nodes: ColumnNode[];
}

/**
 * Lookup entry for O(1) node retrieval across columns
 */
export interface NodeLookupEntry {
  node: ColumnNode;
  columnIndex: number;
}

/**
 * Builds an O(1) lookup map from node ID to its ColumnNode and column index.
 * Eliminates O(n²) linear scans in layout, sorting, and arrow rendering.
 *
 * @param columns - Array of tree columns
 * @returns Map from node ID to { node, columnIndex }
 */
export function buildNodeLookup(columns: TreeColumn[]): Map<string, NodeLookupEntry> {
  const lookup = new Map<string, NodeLookupEntry>();
  for (let colIndex = 0; colIndex < columns.length; colIndex++) {
    for (const node of columns[colIndex].nodes) {
      lookup.set(node.id, { node, columnIndex: colIndex });
    }
  }
  return lookup;
}

/**
 * Groups todo tree nodes into columns by hierarchical depth for visualization
 *
 * This is the core algorithm that transforms a flat todo tree structure into
 * a column-based layout suitable for visual rendering with arrows between nodes.
 *
 * Algorithm:
 * 1. Start from root node and traverse depth-first
 * 2. Group nodes by their depth level into columns
 * 3. Apply visibility filtering (hide done/archived unless explicitly shown)
 * 4. Sort nodes within each column for proper ordering
 * 5. Calculate optimized positions to prevent visual overlap
 *
 * @param tree - Flat todo tree structure (id -> TodoItem mapping)
 * @param rootId - Optional root node ID, will auto-find "Everything" if not provided
 * @param actualHeights - Optional map of actual DOM-measured heights for accurate positioning
 * @returns Array of columns, each containing nodes at the same depth level
 */
export function groupTreeByDepth(tree: TodoTree, rootId?: string, actualHeights?: Map<string, number>): TreeColumn[] {
  const actualRootId = rootId || findRootNodeId(tree);
  if (!actualRootId) {
    return [];
  }

  const columns: Map<number, ColumnNode[]> = new Map();

  function buildColumns(id: string, depth: number, parentId?: string) {
    const item = getItemById(tree, id);
    if (!item) {
      return;
    }

    // Initialize column if it doesn't exist
    if (!columns.has(depth)) {
      columns.set(depth, []);
    }

    // Filter children based on visibility rules
    // This determines which children should be shown in the tree visualization
    const getVisibleAndArchivedChildren = (parentItem: TodoItem): { visibleChildren: string[]; archivedChildren: string[] } => {
      const archivedChildren = parentItem.children.filter(childId => {
        const child = getItemById(tree, childId);
        return child && child.archived;
      });

      switch (parentItem.visibility) {
        case 'hide-all':
          // Hide all children but still show archived in compact view
          return { visibleChildren: [], archivedChildren };

        case 'show-all':
          // Show all non-archived children, archived shown separately
          const nonArchivedChildren = parentItem.children.filter(childId => {
            const child = getItemById(tree, childId);
            return child && !child.archived;
          });
          return { visibleChildren: nonArchivedChildren, archivedChildren };

        case 'show-active':
        default:
          // Default behavior: show active, incomplete tasks, archived shown separately
          const activeChildren = parentItem.children.filter(childId => {
            const child = getItemById(tree, childId);
            if (!child) return false;
            // Show if not done and not archived
            return !child.done && !child.archived;
          });
          return { visibleChildren: activeChildren, archivedChildren };
      }
    };

    const { visibleChildren, archivedChildren } = getVisibleAndArchivedChildren(item);

    // Add node to column
    const columnNode: ColumnNode = {
      id,
      item,
      parentId,
      childrenIds: visibleChildren,
      archivedChildrenIds: archivedChildren,
    };

    columns.get(depth)!.push(columnNode);

    // Process only visible children
    visibleChildren.forEach(childId => {
      buildColumns(childId, depth + 1, id);
    });
  }

  buildColumns(actualRootId, 0);

  // Convert map to sorted array
  const columnsArray = Array.from(columns.keys()).sort((a, b) => a - b).map(depth => ({
    depth,
    nodes: columns.get(depth) || [],
  }));

  // Build lookup map for O(1) node access across all columns
  const lookup = buildNodeLookup(columnsArray);

  // Sort nodes within each column to ensure proper ordering before positioning
  sortNodesWithinColumns(columnsArray, lookup);

  // Calculate optimized positions with actual heights (if available) or estimated heights
  calculateOptimizedPositions(columnsArray, actualHeights, lookup);

  return columnsArray;
}

/**
 * Sorts nodes within each column to maintain proper hierarchical ordering
 *
 * Ensures that:
 * - Nodes with the same parent are grouped together
 * - Sibling order matches the parent's children array order
 * - Nodes from different parents are ordered by their parent's position
 *
 * This prevents visual chaos when multiple parents have children in the same column.
 *
 * @param columns - Array of columns to sort (modified in-place)
 * @param lookup - Pre-built node lookup map for O(1) access
 */
function sortNodesWithinColumns(columns: TreeColumn[], lookup: Map<string, NodeLookupEntry>): void {
  // Sort nodes in each column (except root) by their parent's position and sibling order
  for (let colIndex = 1; colIndex < columns.length; colIndex++) {
    const column = columns[colIndex];
    const parentColumn = columns[colIndex - 1];

    // Build a position map for the parent column: parentId -> index
    const parentPositionMap = new Map<string, number>();
    for (let i = 0; i < parentColumn.nodes.length; i++) {
      parentPositionMap.set(parentColumn.nodes[i].id, i);
    }

    column.nodes.sort((a, b) => {
      const parentAIndex = a.parentId ? parentPositionMap.get(a.parentId) : undefined;
      const parentBIndex = b.parentId ? parentPositionMap.get(b.parentId) : undefined;

      // If parents are different, sort by parent position
      if (a.parentId !== b.parentId && parentAIndex !== undefined && parentBIndex !== undefined) {
        return parentAIndex - parentBIndex;
      }

      // If same parent, sort by sibling order
      if (a.parentId && a.parentId === b.parentId) {
        const parentEntry = lookup.get(a.parentId);
        if (parentEntry) {
          const siblingIndexA = parentEntry.node.childrenIds.indexOf(a.id);
          const siblingIndexB = parentEntry.node.childrenIds.indexOf(b.id);
          return siblingIndexA - siblingIndexB;
        }
      }

      // Fallback: maintain original order
      return 0;
    });
  }
}


/**
 * Single source of truth for resolving a node's height.
 *
 * Prefers actual DOM-measured height (from ResizeObserver) when available,
 * otherwise falls back to a content-based estimate.
 *
 * @param nodeId - UUID of the node
 * @param item - The todo item data
 * @param actualHeights - Optional map of DOM-measured heights
 * @returns Resolved height in pixels
 */
export function getResolvedNodeHeight(
  nodeId: string,
  item: TodoItem,
  actualHeights?: Map<string, number>,
): number {
  return actualHeights?.get(nodeId) ?? estimateNodeHeight(item);
}

/**
 * Estimates the visual height of a node based on its content.
 *
 * The compact node renders as a single dense row (NODE_BASE_HEIGHT). The title
 * wraps and stays fully visible, so a long name adds extra title lines. When the
 * item has a description it also shows a single truncated preview line, adding a
 * fixed DESCRIPTION_PREVIEW_HEIGHT. The full (expanded) description is revealed
 * on demand and its real height is captured by ResizeObserver — so the estimate
 * deliberately stays a tight, deterministic value (it does not scale with
 * description length) and ResizeObserver remains the source of truth.
 *
 * @param item - The todo item to estimate height for
 * @returns Estimated height in pixels
 */
export function estimateNodeHeight(item: TodoItem): number {
  const { NODE_BASE_HEIGHT, DESCRIPTION_PREVIEW_HEIGHT } = LAYOUT;

  // Titles wrap to multiple lines; estimate the extra height so first paint is
  // close before ResizeObserver measures the real wrapped height. Conservative
  // chars-per-line because the name shares its row with the header icons.
  const TITLE_LINE_HEIGHT = 18;
  const charsPerLine = 18; // approx chars beside the header icons at NODE_WIDTH
  const nameLines = Math.max(1, Math.ceil((item.name?.length ?? 0) / charsPerLine));
  const extraTitleHeight = (nameLines - 1) * TITLE_LINE_HEIGHT;

  const hasDescription = !!(item.description && item.description.trim());
  return NODE_BASE_HEIGHT + extraTitleHeight + (hasDescription ? DESCRIPTION_PREVIEW_HEIGHT : 0);
}

/**
 * Calculates the height needed for archived children
 *
 * @param node - The node to calculate archived height for
 * @returns Height in pixels needed for archived children display
 */
function calculateArchivedHeight(node: ColumnNode): number {
  const { ARCHIVED_GROUP_HEIGHT, ARCHIVED_ITEM_HEIGHT, MIN_GAP } = LAYOUT;
  if (node.archivedChildrenIds.length === 0) {
    return 0;
  }
  return ARCHIVED_GROUP_HEIGHT + (node.archivedChildrenIds.length * ARCHIVED_ITEM_HEIGHT) + MIN_GAP;
}

/**
 * Calculates spacing between two nodes based on their type
 *
 * Fixed spacing strategy:
 * - Leaf to leaf: 20px (compact)
 * - Parent to parent: 30px (medium)
 * - Leaf to parent: 30px (medium)
 *
 * @param node1 - First node (can be undefined for boundary cases)
 * @param node2 - Second node (can be undefined for boundary cases)
 * @returns Spacing in pixels between the two nodes
 */
function calculateSpacingBetweenNodes(node1?: ColumnNode, node2?: ColumnNode): number {
  const { LEAF_SPACING, PARENT_SPACING } = LAYOUT;

  if (!node1 || !node2) {
    return PARENT_SPACING;
  }

  const isNode1Leaf = node1.childrenIds.length === 0;
  const isNode2Leaf = node2.childrenIds.length === 0;

  if (isNode1Leaf && isNode2Leaf) {
    // Both are leaf nodes - use compact spacing
    return LEAF_SPACING;
  } else {
    // Any parent involved - use parent spacing
    return PARENT_SPACING;
  }
}

/**
 * A vertical contour of a laid-out subtree, indexed by *relative depth*.
 *
 * Because the tree is drawn in columns (depth = x), each relative level maps to
 * exactly one column: level 0 is the subtree root's own column, level 1 its
 * children's column, and so on. `top[L]` is the minimum top edge and
 * `bottom[L]` the maximum bottom edge (including any archived group that hangs
 * below) of everything the subtree occupies in that column. Two sibling
 * subtrees can never overlap as long as the lower one's `top` clears the upper
 * one's `bottom` at every shared level — which is exactly how they interleave.
 */
interface Contour {
  top: number[];
  bottom: number[];
}

/**
 * A transient node used only while running the contour layout. It mirrors the
 * ColumnNode hierarchy but carries the resolved height and a mutable absolute
 * Y assigned during the walk.
 */
interface LayoutNode {
  columnNode: ColumnNode;
  height: number;
  archivedExtent: number;
  children: LayoutNode[];
  y: number;
}

/**
 * Calculates optimal Y positions for all nodes — a contour-based tidy-tree
 * packer (Reingold–Tilford / Buchheim, rotated and adapted to variable node
 * heights and a fixed column-per-depth layout).
 *
 * Versus the old "reserve a full band per subtree" approach, this lets a shallow
 * sibling subtree nest into the empty space left by a deeper sibling, so ragged
 * trees become markedly shorter while still guaranteeing:
 * - no two nodes overlap within a column (≥ the applicable spacing gap),
 * - sibling order is preserved, so edges never cross,
 * - parents are vertically centred on their children (clean, symmetric arrows),
 * - an archived group's reserved space never collides with the next sibling.
 *
 * Algorithm (single post-order `firstWalk` over a transient tree):
 *   - leaves occupy one level; internal nodes place their children top-to-bottom,
 *     shifting each new child down just enough that its top contour clears the
 *     accumulated bottom contour of the already-placed siblings at every shared
 *     level, then centre the parent on its children's span.
 * Positions are absolute within the root frame, then normalised so the topmost
 * node sits at y = 0 (keeping treeHeight / scroll / fit-to-screen math unchanged).
 *
 * @param columns - Array of columns with nodes to position (modified in-place)
 * @param actualHeights - Optional map of node IDs to measured DOM heights (from ResizeObserver)
 * @param lookup - Pre-built node lookup map for O(1) access
 */
function calculateOptimizedPositions(columns: TreeColumn[], actualHeights: Map<string, number> | undefined, lookup: Map<string, NodeLookupEntry>): void {
  // Clear any existing positions (subtreeHeight is no longer used by the packer
  // and is intentionally left undefined).
  columns.forEach(column => {
    column.nodes.forEach(node => {
      node.yPosition = undefined;
      node.subtreeHeight = undefined;
    });
  });

  if (columns.length === 0 || columns[0].nodes.length === 0) {
    return;
  }

  // Cross-family clearance at deeper levels: subtrees of different parents only
  // need the minimum gap to read cleanly (within-family spacing is already
  // applied at each parent's own level-0 placement below).
  const DEEP_GAP = LAYOUT.MIN_GAP;

  // Build the transient layout tree from the lookup (root(s) live in column 0).
  const build = (columnNode: ColumnNode): LayoutNode => {
    const children: LayoutNode[] = [];
    for (const childId of columnNode.childrenIds) {
      const entry = lookup.get(childId);
      if (entry) {
        children.push(build(entry.node));
      }
    }
    return {
      columnNode,
      height: getResolvedNodeHeight(columnNode.id, columnNode.item, actualHeights),
      archivedExtent: calculateArchivedHeight(columnNode),
      children,
      y: 0,
    };
  };

  // Shift an entire subtree (used when a sibling must move down to clear those above it).
  const shiftSubtree = (node: LayoutNode, delta: number): void => {
    node.y += delta;
    for (const child of node.children) {
      shiftSubtree(child, delta);
    }
  };

  // Post-order placement. Returns this subtree's contour relative to the same
  // frame its node positions are written in.
  const firstWalk = (node: LayoutNode): Contour => {
    if (node.children.length === 0) {
      node.y = 0;
      return { top: [0], bottom: [node.height + node.archivedExtent] };
    }

    // Accumulated contour of already-placed children, in the children's frame
    // (level 0 = the children's column).
    const accumTop: number[] = [];
    const accumBottom: number[] = [];

    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i];
      const childContour = firstWalk(child);

      // Determine how far down this child must move to clear its left siblings.
      let shift = 0;
      if (i > 0) {
        const shared = Math.min(accumBottom.length, childContour.top.length);
        for (let level = 0; level < shared; level++) {
          const gap = level === 0
            ? calculateSpacingBetweenNodes(node.children[i - 1].columnNode, child.columnNode)
            : DEEP_GAP;
          const required = accumBottom[level] + gap - childContour.top[level];
          if (required > shift) {
            shift = required;
          }
        }
      }
      if (shift !== 0) {
        shiftSubtree(child, shift);
      }

      // Merge the (shifted) child contour into the running sibling contour.
      for (let level = 0; level < childContour.top.length; level++) {
        const top = childContour.top[level] + shift;
        const bottom = childContour.bottom[level] + shift;
        if (level < accumTop.length) {
          accumTop[level] = Math.min(accumTop[level], top);
          accumBottom[level] = Math.max(accumBottom[level], bottom);
        } else {
          accumTop[level] = top;
          accumBottom[level] = bottom;
        }
      }
    }

    // Centre the parent on its children's node-box span (children are placed
    // top-to-bottom, so the first child is highest and the last is lowest).
    // Density is unchanged — only the parent's position within its own column
    // moves; the contour clearance that drives packing is independent of it.
    const firstChild = node.children[0];
    const lastChild = node.children[node.children.length - 1];
    const span = (firstChild.y + (lastChild.y + lastChild.height)) / 2;
    node.y = span - node.height / 2;

    // Returned contour: level 0 is this node's own box (plus its archived group),
    // levels 1+ are the children's accumulated contour one column to the right.
    return {
      top: [node.y, ...accumTop],
      bottom: [node.y + node.height + node.archivedExtent, ...accumBottom],
    };
  };

  // Lay out each root (normally a single "Everything" root; multiple are handled
  // defensively by stacking them like top-level siblings).
  const roots = columns[0].nodes.map(build);
  const rootBottom: number[] = [];
  for (let i = 0; i < roots.length; i++) {
    const root = roots[i];
    const contour = firstWalk(root);
    if (i > 0) {
      let shift = 0;
      const shared = Math.min(rootBottom.length, contour.top.length);
      for (let level = 0; level < shared; level++) {
        const gap = level === 0
          ? calculateSpacingBetweenNodes(roots[i - 1].columnNode, root.columnNode)
          : DEEP_GAP;
        const required = rootBottom[level] + gap - contour.top[level];
        if (required > shift) {
          shift = required;
        }
      }
      if (shift !== 0) {
        shiftSubtree(root, shift);
      }
      for (let level = 0; level < contour.bottom.length; level++) {
        const bottom = contour.bottom[level] + shift;
        rootBottom[level] = level < rootBottom.length ? Math.max(rootBottom[level], bottom) : bottom;
      }
    } else {
      for (let level = 0; level < contour.bottom.length; level++) {
        rootBottom[level] = contour.bottom[level];
      }
    }
  }

  // Collect every node and normalise so the topmost sits at y = 0.
  const all: LayoutNode[] = [];
  const collect = (node: LayoutNode): void => {
    all.push(node);
    node.children.forEach(collect);
  };
  roots.forEach(collect);

  let minY = Infinity;
  for (const node of all) {
    if (node.y < minY) {
      minY = node.y;
    }
  }
  if (!isFinite(minY)) {
    minY = 0;
  }

  for (const node of all) {
    node.columnNode.yPosition = node.y - minY;
  }
}

/**
 * Calculates visibility statistics for a parent node's children
 *
 * This function provides detailed metrics about child node visibility based on the parent's
 * visibility settings. It's used to generate the child indicator badges shown next to parent
 * nodes (e.g., "[3/5]" meaning 3 visible out of 5 total children).
 *
 * The visibility rules are:
 * - 'show-active': Show incomplete, non-archived children (default behavior)
 * - 'show-all': Show all non-archived children
 * - 'hide-all': Hide all children but show count
 *
 * @param tree - The flat todo tree structure
 * @param parentId - UUID of the parent node to analyze
 * @returns Object with visibility metrics for UI display
 *
 * @example
 * ```typescript
 * const stats = getChildrenVisibilityStats(tree, parentId);
 * const indicator = `[${stats.visible}/${stats.total}]`;
 * const tooltip = `${stats.hiddenDone} done, ${stats.hiddenArchived} archived`;
 * ```
 */
export function getChildrenVisibilityStats(tree: TodoTree, parentId: string): { visible: number; total: number; hiddenDone: number; hiddenArchived: number } {
  const parent = getItemById(tree, parentId);
  if (!parent) {
    return { visible: 0, total: 0, hiddenDone: 0, hiddenArchived: 0 };
  }

  const total = parent.children.length;

  // Count done and archived children (used across all visibility modes)
  let doneCount = 0;
  let archivedCount = 0;
  let activeCount = 0;

  parent.children.forEach(childId => {
    const child = getItemById(tree, childId);
    if (child) {
      if (child.archived) {
        archivedCount++;
      } else if (child.done) {
        doneCount++;
      } else {
        activeCount++;
      }
    }
  });

  switch (parent.visibility) {
    case 'hide-all':
      return { visible: 0, total, hiddenDone: doneCount, hiddenArchived: archivedCount };

    case 'show-all':
      // show-all shows all non-archived children; archived are always hidden from tree
      return { visible: activeCount + doneCount, total, hiddenDone: 0, hiddenArchived: archivedCount };

    case 'show-active':
    default:
      // show-active: only non-done, non-archived are visible
      return { visible: activeCount, total, hiddenDone: doneCount, hiddenArchived: archivedCount };
  }
}
